import { useState, useCallback, useMemo, useEffect, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';

import { SelectOption } from '~/shared/utils/types';
import { GraphResponse } from '~/shared/types/api';
import { useExportState } from '~/shared/context/ExportStateContext';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { getDateFilterDates } from '~/shared/utils/getDateFilterDates';
import { injectDateParams } from '~/shared/utils/exportUtils';
import { useXFetch } from '~/shared/utils/useXFetch';
import { toaster } from '~/shared/utils/toaster';
import { shouldShowFullGraph } from '~/shared/components/UtilizationCharts/helpers';
import ExportModal, { ExportSection } from '~/shared/components/ExportModal/ExportModal';
import {
  type ExportFormat,
  ExportFilterSection,
  type FilterSelection,
} from '~/shared/components/ExportModal/ExportSections';
import {
  CheckboxOption,
  SelectableOptionCard,
} from '~/shared/components/SelectableOption';
import ChartFilterDropdown from '~/shared/components/ChartFilterDropdown';
import MultiSelectDropdown from '~/shared/components/MultiSelectDropdown';
import DateRangePopup from '~/shared/components/DateRangePopup';
import DateDisplayInput from '~/shared/components/ExportModal/DateDisplayInput';
import TimePartsRow from '~/shared/components/ExportModal/TimePartsRow';
import { useCustomTimeRange } from '~/shared/hooks/useCustomTimeRange';
import { formatDate } from '~/shared/utils/formatNumber';
import { dateToTimeParts, pad } from '~/shared/components/DateRangePopup/helpers';
import {
  CUSTOM_DATES_TITLE,
  dateFilterItems,
} from '~/shared/components/UtilizationCharts/dateFilterMenuItems';
import { LineChartHandle } from '~/shared/components/LineChart/LineChart';
import { reportsFullGraphKey } from '../../common/serachParamKeys';

type DataType = { graphs: boolean; table: boolean };
type GraphExportFormat = 'png' | 'csv' | 'json';
type TableExportFormat = ExportFormat;
type TimeRangeSelection = (typeof dateFilterItems)[number];

interface ReportsExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeFilters: SelectOption[];
  utilizationChartRef: RefObject<LineChartHandle | null>;
  usageChartRef: RefObject<LineChartHandle | null>;
  tableQueryUrl: string;
  tableCountUrl: string;
  chartQueryUrl: string;
  activeDateFilterTitle: string | null;
}

export default function ReportsExportModal({
  isOpen,
  onClose,
  activeFilters,
  utilizationChartRef,
  usageChartRef,
  tableQueryUrl,
  tableCountUrl,
  chartQueryUrl,
  activeDateFilterTitle,
}: ReportsExportModalProps) {
  const { triggerReportsExport } = useExportState();
  const { activeTenantId } = useAuthContext();
  const [searchParams] = useSearchParams();
  const xfetch = useXFetch();

  const [tableFormats, setTableFormats] = useState<TableExportFormat[]>(['csv']);
  const [graphFormats, setGraphFormats] = useState<GraphExportFormat[]>(['csv']);
  const [filterSelection, setFilterSelection] = useState<FilterSelection>('with_filters');
  const [dataType, setDataType] = useState<DataType>({ graphs: true, table: true });
  const [isCapturingGraphs, setIsCapturingGraphs] = useState(false);
  const [timeRangeSelection, setTimeRangeSelection] = useState<TimeRangeSelection>(() => {
    if (activeDateFilterTitle) {
      if (activeDateFilterTitle.includes(CUSTOM_DATES_TITLE)) {
        return { title: activeDateFilterTitle } as TimeRangeSelection;
      }
      const found = dateFilterItems.find((item) => item.title === activeDateFilterTitle);
      if (found) return found;
    }
    return dateFilterItems.find((item) => item.title === 'Last 3 hours') || dateFilterItems[0];
  });
  const [isDatePopupOpen, setIsDatePopupOpen] = useState(false);

  const [selectedStartDate, selectedEndDate] = useMemo(
    () => getDateFilterDates(timeRangeSelection.duration, timeRangeSelection.title),
    [timeRangeSelection]
  );

  const setTimeRangeTitle = useCallback(
    (title: string) => setTimeRangeSelection({ title } as TimeRangeSelection),
    []
  );

  const {
    startTimeParts,
    endTimeParts,
    customTimeError,
    customRangeError,
    patchStartTimeParts,
    patchEndTimeParts,
    setStartTimeParts,
    setEndTimeParts,
    resetErrors,
  } = useCustomTimeRange({ selectedStartDate, selectedEndDate, setTimeRangeTitle });

  useEffect(() => {
    const start = dateToTimeParts(selectedStartDate);
    const end = dateToTimeParts(selectedEndDate);
    setStartTimeParts({
      hours: pad(start.hours),
      minutes: pad(start.minutes),
      isPm: start.isPm,
    });
    setEndTimeParts({
      hours: pad(end.hours),
      minutes: pad(end.minutes),
      isPm: end.isPm,
    });
    resetErrors();
  }, [selectedStartDate, selectedEndDate, setStartTimeParts, setEndTimeParts, resetErrors]);

  const handleCustomDateSubmit = useCallback(
    (startDate: Date, endDate: Date) => {
      setTimeRangeSelection({
        title: `${CUSTOM_DATES_TITLE}|${startDate.toISOString()}|${endDate.toISOString()}`,
      } as TimeRangeSelection);
      setIsDatePopupOpen(false);
    },
    []
  );

  const captureGraphPngs = useCallback(async () => {
    const exportChartUrl = injectDateParams(chartQueryUrl, selectedStartDate, selectedEndDate);
    const graphResponse = await xfetch<GraphResponse>(exportChartUrl);
    const graphData = graphResponse.utilizationGraph ?? [];

    const chartConfigs = [
      { ref: utilizationChartRef, name: 'Utilization_Chart' as const },
      { ref: usageChartRef, name: 'Usage_Chart' as const },
    ];

    const preRenderedGraphPngs: Array<{ name: 'Utilization_Chart' | 'Usage_Chart'; blob: Blob }> = [];
    for (const { ref, name } of chartConfigs) {
      const blob = await ref.current?.exportWithData(graphData);
      if (!blob) {
        throw new Error('Could not export chart image.');
      }
      preRenderedGraphPngs.push({ name, blob });
    }

    return preRenderedGraphPngs;
  }, [chartQueryUrl, selectedStartDate, selectedEndDate, xfetch, utilizationChartRef, usageChartRef]);

  const handleExport = useCallback(async () => {
    try {
      const shouldCaptureGraphs = dataType.graphs && graphFormats.includes('png');
      if (shouldCaptureGraphs) {
        setIsCapturingGraphs(true);
      }

      const preRenderedGraphPngs = shouldCaptureGraphs ? await captureGraphPngs() : undefined;
      const showFullGraph = shouldShowFullGraph(
        !!searchParams.get(reportsFullGraphKey),
        activeTenantId
      );
      onClose();

      triggerReportsExport({
        activeFilters,
        filterSelection,
        showFullGraph,
        dataType,
        graphFormats,
        tableFormats,
        selectedStartDate,
        selectedEndDate,
        tableQueryUrl,
        tableCountUrl,
        chartQueryUrl,
        preRenderedGraphPngs,
        utilizationChartRef,
        usageChartRef,
      });
    } catch {
      toaster({
        variant: 'error',
        title: 'Export failed',
        message: 'Please try again.',
      });
    } finally {
      setIsCapturingGraphs(false);
    }
  }, [
    onClose,
    triggerReportsExport,
    searchParams,
    activeTenantId,
    filterSelection,
    activeFilters,
    dataType,
    tableFormats,
    graphFormats,
    selectedStartDate,
    selectedEndDate,
    utilizationChartRef,
    usageChartRef,
    tableQueryUrl,
    tableCountUrl,
    chartQueryUrl,
    captureGraphPngs,
  ]);

  const isCustomDateSelected = useMemo(
    () => timeRangeSelection.title?.includes(CUSTOM_DATES_TITLE) ?? false,
    [timeRangeSelection.title]
  );

  const neitherSelected = !dataType.graphs && !dataType.table;
  const missingFormatsForSelectedType =
    (dataType.graphs && graphFormats.length === 0) || (dataType.table && tableFormats.length === 0);
  const hasCustomDateError = isCustomDateSelected && !!(customTimeError || customRangeError);
  const isExportDisabled = neitherSelected || missingFormatsForSelectedType || isCapturingGraphs || hasCustomDateError;

  return (
    <ExportModal
      isOpen={isOpen}
      onClose={onClose}
      title="Export"
      onExport={handleExport}
      isExporting={isCapturingGraphs}
      isExportDisabled={isExportDisabled}
    >
      <ExportSection label="Content">
        <div className="flex flex-col gap-2 mb-4">
          <SelectableOptionCard
            inputId="rep-type-graphs"
            checked={dataType.graphs}
            label="Graphs"
            description="Utilization and Usage data"
            onSelect={() => setDataType((prev) => ({ ...prev, graphs: !prev.graphs }))}
          >
            <CheckboxOption
              id="rep-type-graphs"
              checked={dataType.graphs}
              onChange={(v) => setDataType((prev) => ({ ...prev, graphs: v }))}
            />
          </SelectableOptionCard>
          <MultiSelectDropdown
            items={['CSV', 'JSON', 'PNG']}
            selectedItems={graphFormats.map((format) => format.toUpperCase())}
            onChange={(items) =>
              setGraphFormats(items.map((item) => item.toLowerCase() as GraphExportFormat))
            }
            placeholder="Select format"
            aria-label="Graph export format"
          />
        </div>
        <div className="flex flex-col gap-2 mb-4">
          <SelectableOptionCard
            inputId="rep-type-table"
            checked={dataType.table}
            label="Table"
            description="Optimization Actions records"
            onSelect={() => setDataType((prev) => ({ ...prev, table: !prev.table }))}
          >
            <CheckboxOption
              id="rep-type-table"
              checked={dataType.table}
              onChange={(v) => setDataType((prev) => ({ ...prev, table: v }))}
            />
          </SelectableOptionCard>
          <MultiSelectDropdown
            items={['CSV', 'JSON']}
            selectedItems={tableFormats.map((format) => format.toUpperCase())}
            onChange={(items) =>
              setTableFormats(items.map((item) => item.toLowerCase() as TableExportFormat))
            }
            placeholder="Select format"
            aria-label="Table export format"
          />
        </div>
      </ExportSection>

      <ExportFilterSection
        groupName="rep-filters"
        value={filterSelection}
        onChange={setFilterSelection}
      />

      <ExportSection label="Time range" borderBottom={false}>
        <div className="col-span-2 flex flex-col gap-4">
          <ChartFilterDropdown
            align="top-left"
            fullWidth
            portal
            items={dateFilterItems}
            onSelect={(item) => setTimeRangeSelection(item)}
            selectedItem={timeRangeSelection}
            delegateCustomDateCalendar
            onDelegatedCustomDateOpen={() => setIsDatePopupOpen(true)}
          />
          {isCustomDateSelected && (
            <div className="flex flex-col">
              <div className="flex gap-4">
                <div>
                  <DateDisplayInput
                    label="Start Date"
                    value={formatDate(selectedStartDate)}
                    onCalendarClick={() => setIsDatePopupOpen(true)}
                  />
                  <TimePartsRow
                    label=""
                    value={startTimeParts}
                    onChange={patchStartTimeParts}
                  />
                </div>
                <div>
                  <DateDisplayInput
                    label="End Date"
                    value={formatDate(selectedEndDate)}
                    onCalendarClick={() => setIsDatePopupOpen(true)}
                  />
                  <TimePartsRow
                    label=""
                    value={endTimeParts}
                    onChange={patchEndTimeParts}
                  />
                </div>
              </div>
              {customTimeError && (
                <p className="text-sm text-red" role="alert">{customTimeError}</p>
              )}
              {customRangeError && (
                <p className="text-sm text-red" role="alert">{customRangeError}</p>
              )}
            </div>
          )}
        </div>
      </ExportSection>
      {isDatePopupOpen &&
        createPortal(
          <div
            className="rounded-md bg-white border border-primary-70 shadow-datepicker"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 99999,
              boxShadow: '0px 10px 30px rgba(14, 60, 72, 0.16)',
            }}
          >
            <DateRangePopup
              startDateStr={selectedStartDate}
              endDateStr={selectedEndDate}
              onClose={() => setIsDatePopupOpen(false)}
              onSubmit={handleCustomDateSubmit}
            />
          </div>,
          document.body
        )}
    </ExportModal>
  );
}
